import { create } from "zustand";
import { combine } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import {
  computeStaticInterferenceFromCurrentScene,
  type StaticInterferenceResult,
} from "../physics/staticInterferenceProbe";
import type { Vector3 } from "../types/scene";

export type MeasurementTool = "distance" | "angle";
export type MeasurementPoint = {
  id: string;
  position: Vector3;
  bodyUuid: string | null;
  label: string | null;
};
export type DistanceMeasurement = {
  id: string;
  type: "distance";
  points: [MeasurementPoint, MeasurementPoint];
  meters: number;
};
export type AngleMeasurement = {
  id: string;
  type: "angle";
  points: [MeasurementPoint, MeasurementPoint, MeasurementPoint];
  degrees: number;
};
export type Measurement = DistanceMeasurement | AngleMeasurement;
export type StaticInterferenceStatus = "idle" | "checking" | "ready" | "error";

type MeasurementState = {
  activeTool: MeasurementTool | null;
  currentPoints: MeasurementPoint[];
  measurements: Measurement[];
  staticInterferenceStatus: StaticInterferenceStatus;
  staticInterferenceResult: StaticInterferenceResult | null;
  staticInterferenceError: string | null;
};

type MeasurementActions = {
  setActiveTool: (tool: MeasurementTool | null) => void;
  addSurfacePoint: (point: Omit<MeasurementPoint, "id">) => void;
  clearMeasurements: () => void;
  clearStaticInterference: () => void;
  computeStaticInterference: () => Promise<void>;
};

const nextId = (prefix: string) =>
  `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const vectorDistance = (a: Vector3, b: Vector3) => {
  const dx = Number(a[0]) - Number(b[0]);
  const dy = Number(a[1]) - Number(b[1]);
  const dz = Number(a[2]) - Number(b[2]);
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
};

const vectorAngleDegrees = (a: Vector3, b: Vector3, c: Vector3) => {
  const ab = [
    Number(a[0]) - Number(b[0]),
    Number(a[1]) - Number(b[1]),
    Number(a[2]) - Number(b[2]),
  ];
  const cb = [
    Number(c[0]) - Number(b[0]),
    Number(c[1]) - Number(b[1]),
    Number(c[2]) - Number(b[2]),
  ];
  const abLen = Math.hypot(ab[0], ab[1], ab[2]);
  const cbLen = Math.hypot(cb[0], cb[1], cb[2]);
  if (abLen <= 0 || cbLen <= 0) return 0;
  const dot = (ab[0] * cb[0] + ab[1] * cb[1] + ab[2] * cb[2]) / (abLen * cbLen);
  return (Math.acos(Math.max(-1, Math.min(1, dot))) * 180) / Math.PI;
};

const initialState = (): MeasurementState => ({
  activeTool: null,
  currentPoints: [],
  measurements: [],
  staticInterferenceStatus: "idle",
  staticInterferenceResult: null,
  staticInterferenceError: null,
});

const useInternalMeasurementStore = create<MeasurementState & { actions: MeasurementActions }>()(
  combine(initialState(), (set, get) => ({
    actions: {
      setActiveTool: (tool) => set({ activeTool: tool, currentPoints: [] }),
      addSurfacePoint: (point) => {
        const activeTool = get().activeTool;
        if (!activeTool) return;

        const nextPoint: MeasurementPoint = {
          ...point,
          id: nextId("point"),
          bodyUuid: point.bodyUuid || null,
          label: point.label || null,
        };
        const nextPoints = [...get().currentPoints, nextPoint];
        if (activeTool === "distance" && nextPoints.length >= 2) {
          const points = nextPoints.slice(0, 2) as [MeasurementPoint, MeasurementPoint];
          set((state) => ({
            currentPoints: [],
            measurements: [
              ...state.measurements,
              {
                id: nextId("distance"),
                type: "distance",
                points,
                meters: vectorDistance(points[0].position, points[1].position),
              },
            ],
          }));
          return;
        }
        if (activeTool === "angle" && nextPoints.length >= 3) {
          const points = nextPoints.slice(0, 3) as [
            MeasurementPoint,
            MeasurementPoint,
            MeasurementPoint,
          ];
          set((state) => ({
            currentPoints: [],
            measurements: [
              ...state.measurements,
              {
                id: nextId("angle"),
                type: "angle",
                points,
                degrees: vectorAngleDegrees(
                  points[0].position,
                  points[1].position,
                  points[2].position
                ),
              },
            ],
          }));
          return;
        }
        set({ currentPoints: nextPoints });
      },
      clearMeasurements: () => set({ currentPoints: [], measurements: [] }),
      clearStaticInterference: () =>
        set({
          staticInterferenceStatus: "idle",
          staticInterferenceResult: null,
          staticInterferenceError: null,
        }),
      computeStaticInterference: async () => {
        if (get().staticInterferenceStatus === "checking") return;
        set({
          staticInterferenceStatus: "checking",
          staticInterferenceError: null,
        });
        try {
          const result = await computeStaticInterferenceFromCurrentScene();
          set({
            staticInterferenceStatus: "ready",
            staticInterferenceResult: result,
            staticInterferenceError: null,
          });
        } catch (error) {
          set({
            staticInterferenceStatus: "error",
            staticInterferenceError: String(error instanceof Error ? error.message : error),
          });
        }
      },
    },
  }))
);

export const getMeasurementState = () => useInternalMeasurementStore.getState();
export const useMeasurementToolState = () =>
  useInternalMeasurementStore(
    useShallow((state) => ({
      activeTool: state.activeTool,
      currentPoints: state.currentPoints,
      measurements: state.measurements,
    }))
  );
export const useStaticInterferenceState = () =>
  useInternalMeasurementStore(
    useShallow((state) => ({
      status: state.staticInterferenceStatus,
      result: state.staticInterferenceResult,
      error: state.staticInterferenceError,
    }))
  );
export const useMeasurementActions = () => useInternalMeasurementStore((state) => state.actions);
